import { useState } from "react";
import { Cog, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * VehicleCarousel — mobile-first swipeable image container for public
 * vehicle cards and booking modal. Touch swipe (native scroll + snap),
 * hover chevrons on desktop, discreet 1/N counter pill bottom-right.
 */
export default function VehicleCarousel({ images, alt, vehicleId, aspect = "aspect-[16/10]" }) {
  const [idx, setIdx] = useState(0);
  const total = images.length;
  const clamp = (i) => (total ? (i + total) % total : 0);
  const go = (dir) => setIdx((i) => clamp(i + dir));

  if (total === 0) {
    return (
      <div className={`${aspect} bg-neutral-100 flex items-center justify-center relative`}>
        <Cog size={32} className="text-neutral-300" />
      </div>
    );
  }

  return (
    <div
      className={`${aspect} bg-neutral-100 relative group overflow-hidden`}
      data-testid={`pb-carousel-${vehicleId}`}
      onTouchStart={(e) => (e.currentTarget.__x = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        const dx = (e.changedTouches[0].clientX - (e.currentTarget.__x || 0));
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      }}
    >
      {images.map((src, i) => (
        <img
          key={`${vehicleId}-${i}`}
          src={src}
          alt={`${alt} ${i + 1}`}
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        />
      ))}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous image"
            data-testid={`pb-carousel-prev-${vehicleId}`}
            className="hidden md:flex absolute top-1/2 left-2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/85 text-neutral-800 shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next image"
            data-testid={`pb-carousel-next-${vehicleId}`}
            className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/85 text-neutral-800 shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
          >
            <ChevronRight size={16} />
          </button>
          <span
            className="absolute bottom-2 right-2 text-[10px] font-medium tracking-wide px-2 py-0.5 rounded-full bg-black/55 text-white tabular-nums"
            data-testid={`pb-carousel-counter-${vehicleId}`}
          >
            {idx + 1}/{total}
          </span>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid={`pb-carousel-dots-${vehicleId}`}>
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                aria-label={`Show image ${i + 1}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === idx ? "bg-white w-3" : "bg-white/60 hover:bg-white/90"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
